import React, { useState, useEffect } from 'react';
import {
  Alert,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Input,
  message,
  Descriptions,
  Progress
} from 'antd';
import {
  ArrowLeftOutlined,
  ExportOutlined,
  CheckOutlined,
  DownloadOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { useParams, useNavigate } from 'react-router-dom';
import DspBillPaymentInfoCard from '../../../components/settlement/DspBillPaymentInfoCard';
import DspInvoiceCard from '../../../components/settlement/DspInvoiceCard';
import BillAttachmentPanel from '../../../components/settlement/BillAttachmentPanel';
import { useMyDspBillPaymentInfo } from '../../../graphql/queries/settlement/dspBillPayment';
import {
  GET_DSP_ADJUST_BILL_TASK,
  DspAdjustBillTask,
  GetDspAdjustBillTaskData,
  GetDspAdjustBillTaskVariables,
} from '../../central/settlement/graphql/queries/getDspAdjustBillTask';
import { renderBillTypeTag, getBillTypeLabel, getBillStatusColor, translateFeeItemName, translateCurrentStep } from '../../../utils/billI18n';
import { GET_DELIVERY_DSP_BILL_DETAIL } from '../../../graphql/queries/settlement/getSettlementBillQueries';
import {
  CONFIRM_BILL,
  EXPORT_BILL,
  REJECT_BILL_EXTERNAL
} from '../../../graphql/mutations/settlement/settlementBillMutations';
import { GET_EXPORT_HISTORY, GET_EXPORT_FILE_DOWNLOAD_URL } from '../../../graphql/queries/settlement/billExportQueries';
import { BillExportModal, type ExportLanguage } from '../../../components/settlement/BillExportModal';
import BillExportProgressModal from '../../../components/settlement/BillExportProgressModal';
import { downloadBillExportFile, formatFileSize, formatExportStatus } from '../../../utils/billExportDownload';
import { formatFileType, getFileTypeColor } from '../../../utils/fileTypeFormatter';
import { formatTimeWithTimezone } from '../../../utils/timezoneFormatter';
import { canExportBill, getExportButtonTitle } from '../../../utils/billExportPermission';
import { formatSettlementAmountFromMajor } from '../../../utils/settlementAmountFormatter';
import { getUserInfo } from '../../../utils/auth';
import type {
  SettlementBillDetailResponseType,
  BillFeeSummaryType,
  BillExportTemplateType
} from '../../../graphql/types/settlement';
import type { BillExportHistoryType } from '../../../graphql/types/settlement/BillExportTypes';
import { useTranslation } from 'react-i18next';

/** Delivery DSP 账单详情（DSP_MANAGER） */
const DeliveryDspBillDetailPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { billId } = useParams<{ billId: string }>();
  const navigate = useNavigate();
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [progressModalVisible, setProgressModalVisible] = useState(false);
  const [exportTaskId, setExportTaskId] = useState<string | null>(null);

  const { data: taskData } = useQuery<GetDspAdjustBillTaskData, GetDspAdjustBillTaskVariables>(
    GET_DSP_ADJUST_BILL_TASK,
    {
      variables: { billId: billId ?? "" },
      skip: !billId,
    }
  );
  const latestTask: DspAdjustBillTask | null = taskData?.getDspAdjustBillTask ?? null;
  const isTaskInProgress = latestTask !== null && (latestTask.status === 0 || latestTask.status === 1);

  const { data, loading, refetch } = useQuery(GET_DELIVERY_DSP_BILL_DETAIL, {
    variables: { billId },
    fetchPolicy: 'cache-and-network',
    onError: (err) => {
      message.error(`${t('dspBill.detail.queryFailed')}: ${err.message}`);
    }
  });

  // DSP Owner 侧付款信息：只读，查看的是自身财务档案（全量值）
  const { data: paymentInfoData, loading: paymentInfoLoading } = useMyDspBillPaymentInfo(billId);

  const [loadExportHistory, { data: exportHistoryData, loading: exportHistoryLoading }] = useLazyQuery(
    GET_EXPORT_HISTORY,
    {
      variables: {
        billId: billId,
        clientId: null,
        pageRequest: { page: 1, pageSize: 20 }
      },
      fetchPolicy: 'network-only'
    }
  );

  const [getExportFileDownloadUrl] = useLazyQuery(GET_EXPORT_FILE_DOWNLOAD_URL, {
    fetchPolicy: 'network-only'
  });

  const [confirmBill, { loading: confirmBillLoading }] = useMutation(CONFIRM_BILL, {
    refetchQueries: ['GetDeliveryDspBillDetail'],
    awaitRefetchQueries: true
  });
  const [rejectBillExternal] = useMutation(REJECT_BILL_EXTERNAL, {
    refetchQueries: ['GetDeliveryDspBillDetail'],
    awaitRefetchQueries: true
  });
  const [exportBill] = useMutation(EXPORT_BILL);

  const billDetail: SettlementBillDetailResponseType | undefined = data?.getDeliveryDspBillDetail;

  useEffect(() => {
    if (billId) {
      loadExportHistory();
    }
  }, [billId, loadExportHistory]);

  const roleName = getUserInfo()?.account?.role?.name;
  const isDspOwnerRole = (roleName as string) === 'DSP_OWNER_TEAM' || (roleName as string) === 'DSP_OWNER';
  const canConfirmByCurrentRole = !!billDetail?.bill && (() => {
    const status = billDetail.bill.status;
    if (isDspOwnerRole) {
      return status === 'INTERNAL_CONFIRMED' || status === 'EXTERNAL_REJECTED';
    }
    return false;
  })();
  const canRejectByCurrentRole = !!billDetail?.bill && billDetail.bill.status === 'INTERNAL_CONFIRMED';
  const showConfirmBillButton = !!billDetail?.bill &&
    billDetail.bill.status === 'INTERNAL_CONFIRMED' &&
    !billDetail.bill.isCanceled;

  const handleRefreshExportHistory = () => loadExportHistory();

  const handleDownloadExport = async (record: BillExportHistoryType) => {
    if (record.status !== 'SUCCESS') {
      message.warning(t('dspBill.exportHistory.downloadOnlySuccess'));
      return;
    }
    if (!record.fileName) {
      message.error(t('dspBill.exportHistory.fileNotFound'));
      return;
    }
    try {
      message.loading({ content: t('dspBill.exportHistory.gettingDownloadUrl'), key: 'download' });
      const result = await getExportFileDownloadUrl({
        variables: { taskId: record.taskId }
      });
      const downloadUrl = result.data?.getExportFileDownloadUrl;
      if (!downloadUrl) {
        message.error({ content: t('dspBill.exportHistory.getDownloadUrlFailed'), key: 'download' });
        return;
      }
      message.success({ content: t('dspBill.exportHistory.startDownload'), key: 'download', duration: 2 });
      downloadBillExportFile(downloadUrl, record.fileName);
    } catch (err) {
      message.error(`${t('dspBill.exportHistory.getDownloadUrlFailed')}: ${(err as Error).message}`);
    }
  };

  const handleConfirmBill = () => {
    Modal.confirm({
      title: t('dspBill.confirmModal.title'),
      content: t('dspBill.confirmModal.content'),
      okText: t('dspBill.confirmModal.okText'),
      cancelText: t('dspBill.confirmModal.cancelText'),
      onOk: async () => {
        try {
          const result = await confirmBill({ variables: { billId } });
          if (result.data?.confirmBill?.success) {
            message.success(t('dspBill.confirmModal.confirmSuccess'));
            await refetch();
          } else {
            message.error(result.data?.confirmBill?.errorMessage || t('dspBill.confirmModal.confirmFailed'));
          }
        } catch (err) {
          message.error(`${t('dspBill.confirmModal.confirmFailed')}: ${(err as Error).message}`);
        }
      }
    });
  };

  const handleRejectBillExternal = async () => {
    let remark = '';
    Modal.confirm({
      title: t('dspBill.rejectModal.title'),
      content: (
        <div className="pb-6">
          <p className="mb-2">
            {t('dspBill.rejectModal.instruction')}
          </p>
          <Input.TextArea
            rows={4}
            maxLength={500}
            showCount
            placeholder={t('dspBill.rejectModal.placeholder') as string}
            onChange={(e) => {
              remark = e.target.value;
            }}
          />
        </div>
      ),
      okText: t('dspBill.rejectModal.okText'),
      cancelText: t('dspBill.rejectModal.cancelText'),
      okType: 'danger',
      onOk: async () => {
        if (!remark.trim()) {
          message.warning(t('dspBill.rejectModal.reasonRequired'));
          return Promise.reject(new Error('remark required'));
        }
        try {
          const result = await rejectBillExternal({ variables: { billId, remark: remark.trim() } });
          if (result.data?.rejectBillExternal?.success) {
            message.success(t('dspBill.rejectModal.rejectSuccess'));
            await refetch();
          } else {
            message.error(result.data?.rejectBillExternal?.errorMessage || t('dspBill.rejectModal.rejectFailed'));
            return Promise.reject(new Error('reject failed'));
          }
        } catch (err) {
          message.error(`${t('dspBill.rejectModal.rejectFailed')}: ${(err as Error).message}`);
          return Promise.reject(err as Error);
        }
      }
    });
  };

  const handleExportBill = () => setExportModalVisible(true);

  const handleExport = async (template: BillExportTemplateType, language: ExportLanguage) => {
    if (!billDetail?.bill) return;
    const bill = billDetail.bill;
    try {
      message.loading({ content: t('dspBill.exportModal.checkingHistory'), key: 'check', duration: 0 });
      const { data: historyData } = await loadExportHistory();
      message.destroy('check');
      const historyRecords = historyData?.getExportHistory?.content || [];
      const successRecords = historyRecords.filter((r: any) => r.status === 'SUCCESS');

      if (successRecords.length > 0) {
        const latestRecord = successRecords[0];
        const confirmResult = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: t('dspBill.exportModal.confirmTitle'),
            content: (
              <div>
                <p>{t('dspBill.exportModal.hasHistoryTip')}</p>
                <ul style={{ marginTop: 8, marginLeft: 20 }}>
                  <li>{t('dspBill.exportModal.exportTime')}: {new Date(latestRecord.createdAt).toLocaleString(i18n.language)}</li>
                  <li>{t('dspBill.exportModal.fileName')}: {latestRecord.fileName}</li>
                  <li>{t('dspBill.exportModal.templateType')}: {latestRecord.templateType}</li>
                </ul>
                <p style={{ marginTop: 12, color: '#faad14' }}>
                  <strong>{t('dspBill.exportModal.tipPrefix')}</strong>{t('dspBill.exportModal.reExportNote')}
                </p>
                <p>{t('dspBill.exportModal.continueExport')}</p>
              </div>
            ),
            okText: t('dspBill.exportModal.okText'),
            cancelText: t('dspBill.exportModal.cancelText'),
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
            width: 520
          });
        });
        if (!confirmResult) return;
      } else {
        const confirmResult = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            title: t('dspBill.exportModal.confirmTitle'),
            content: (
              <div>
                <p>{t('dspBill.exportModal.newExportTip')}</p>
                <ul style={{ marginTop: 8, marginLeft: 20 }}>
                  <li>{t('dspBill.fields.billId')}: {bill.id}</li>
                  <li>{t('dspBill.fields.dspCustomer')}: {bill.clientName}</li>
                  <li>{t('dspBill.fields.billType')}: {getBillTypeLabel(bill.billType)}</li>
                  <li>{t('dspBill.fields.billAmount')}: {formatSettlementAmountFromMajor(bill.billAmount, bill.currency)}</li>
                </ul>
                <p style={{ marginTop: 12 }}>{t('dspBill.exportModal.confirmExport')}</p>
              </div>
            ),
            okText: t('dspBill.exportModal.okText'),
            cancelText: t('dspBill.exportModal.cancelText'),
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });
        if (!confirmResult) return;
      }

      const result = await exportBill({
        variables: {
          exportRequest: { billId: bill.id, template, format: 'EXCEL', language }
        }
      });
      if (result.data?.exportBill?.success) {
        const taskIds = result.data.exportBill.taskIds || [];
        message.success(t('dspBill.exportModal.taskCreated', { count: taskIds.length }));
        setExportModalVisible(false);
        setExportTaskId(taskIds.length > 0 ? taskIds.join(',') : '');
        setProgressModalVisible(true);
      } else {
        message.error(result.data?.exportBill?.errorMessage || t('dspBill.exportModal.exportFailed'));
      }
    } catch (err) {
      message.destroy('check');
      message.error(`${t('dspBill.exportModal.exportFailed')}: ${(err as Error).message}`);
    }
  };

  const handleExportCompleted = (task: any) => {
    if (task.status === 'SUCCESS') message.success(t('dspBill.exportModal.exportSuccess'));
    else if (task.status === 'FAILED') message.error(`${t('dspBill.exportModal.exportFailed')}: ${task.errorMessage || t('dspBill.exportModal.unknownError')}`);
  };

  if (loading) {
    return <div>{t('dspBill.detail.loading')}</div>;
  }
  if (!billDetail) {
    return <div>{t('dspBill.detail.notFound')}</div>;
  }

  const { bill, feeSummaries } = billDetail;
  const exportHistory = exportHistoryData?.getExportHistory?.content || [];
  const hiddenFeeItemIdsForRoles = new Set(['89', '107']);
  const shouldHideSomeFeeItems = roleName === 'DSP_OWNER_TEAM' || roleName === 'HUB_MANAGER';
  const visibleFeeSummaries = shouldHideSomeFeeItems
    ? feeSummaries.filter((item) => !hiddenFeeItemIdsForRoles.has(String(item.feeItemId)))
    : feeSummaries;
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formatTime = (timeString: string | undefined | null) => {
    if (!timeString) return '-';
    return formatTimeWithTimezone(timeString, browserTimeZone);
  };
  const formatBillPeriodTime = (timeString: string | undefined | null, timezone: string) => {
    return formatTimeWithTimezone(timeString, timezone, i18n.language);
  };

  const feeSummaryColumns = [
    { title: t('dspBill.fields.feeItemName'), dataIndex: 'feeItemName', key: 'feeItemName', render: (name: string) => translateFeeItemName(name) },
    { title: t('dspBill.fields.totalAmount'), dataIndex: 'totalAmount', key: 'totalAmount', render: (amount: string, record: BillFeeSummaryType) => (
        <span style={{ color: record.amountSign === -1 ? '#ff4d4f' : '#52c41a' }}>
          {`${record.amountSign === -1 ? '-' : '+'}${amount}`}
        </span>
      ) },
  ];

  const exportHistoryColumns = [
    { title: t('dspBill.exportHistory.taskId'), dataIndex: 'taskId', key: 'taskId', width: 80 },
    {
      title: t('dspBill.exportHistory.fileType'),
      dataIndex: 'fileType',
      key: 'fileType',
      width: 100,
      render: (fileType: number) => {
        const color = getFileTypeColor(fileType as 0 | 1 | 2);
        return <Tag color={color}>{formatFileType(fileType as 0 | 1 | 2)}</Tag>;
      }
    },
    { title: t('dspBill.exportHistory.fileName'), dataIndex: 'fileName', key: 'fileName', width: 250, ellipsis: true },
    { title: t('dspBill.exportHistory.fileSize'), dataIndex: 'fileSize', key: 'fileSize', width: 100, render: (size: number | undefined) => (size ? formatFileSize(size) : '-') },
    {
      title: t('dspBill.exportHistory.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const config = formatExportStatus(status);
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: t('dspBill.exportHistory.progress'),
      dataIndex: 'progress',
      key: 'progress',
      width: 150,
      render: (progress: number, record: BillExportHistoryType) => {
        const getStatus = () => {
          if (record.status === 'RUNNING') return 'active';
          if (record.status === 'SUCCESS') return 'success';
          if (record.status === 'FAILED') return 'exception';
          return 'normal';
        };
        return <Progress percent={progress} status={getStatus()} format={(p) => `${p}%`} size="small" />;
      }
    },
    { title: t('dspBill.exportHistory.currentStep'), dataIndex: 'currentStep', key: 'currentStep', width: 200, ellipsis: true, render: (step: string | undefined) => translateCurrentStep(step) },
    { title: t('dspBill.exportHistory.createdAt'), dataIndex: 'createdAt', key: 'createdAt', width: 170, render: (time: string) => formatTime(time) },
    { title: t('dspBill.exportHistory.completedAt'), dataIndex: 'completedAt', key: 'completedAt', width: 170, render: (time: string | undefined) => formatTime(time) },
    {
      title: t('dspBill.fields.action'),
      key: 'actions',
      width: 80,
      fixed: 'right' as const,
      render: (_: any, record: BillExportHistoryType) => (
        <Button
          type="link"
          size="small"
          icon={<DownloadOutlined />}
          disabled={record.status !== 'SUCCESS'}
          onClick={() => handleDownloadExport(record)}
        >
          {t('dspBill.exportHistory.download')}
        </Button>
      )
    }
  ];

  return (
    <div className="p-6">
      {bill.rejectReason && !bill.invoiceSubmitted && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('dspInvoice.reject.reasonBanner', { reason: bill.rejectReason })}
        />
      )}
      {isTaskInProgress && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('dspBill.detail.adjustTaskInProgressBanner')}
        />
      )}
      <div className="mb-4">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/delivery/dsp-bill')}
          className="mb-2"
        >
          {t('common.back')}
        </Button>
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">{t('dspBill.detail.title')}</h1>
          <Space>
            {bill.invoiceEligible && (
              <DspInvoiceCard billId={billId!} billStatus={bill.status} rejectReason={bill.rejectReason} />
            )}
            {showConfirmBillButton && (
              <Button
                type="primary"
                icon={<CheckOutlined />}
                onClick={handleConfirmBill}
                loading={confirmBillLoading}
                disabled={confirmBillLoading}
              >
                {t('dspBill.list.confirmBill')}
              </Button>
            )}
            {canRejectByCurrentRole && !bill.isCanceled && (
              <Button danger onClick={handleRejectBillExternal}>
                {t('dspBill.list.rejectBill')}
              </Button>
            )}
            <Button
              icon={<ExportOutlined />}
              onClick={handleExportBill}
              disabled={!canExportBill(bill) || isTaskInProgress}
              title={isTaskInProgress ? t('dspBill.detail.exportDisabledByAdjustTask') as string : getExportButtonTitle(bill)}
            >
              {t('dspBill.list.exportBill')}
            </Button>
          </Space>
        </div>
      </div>

      <Card title={t('dspBill.detail.billInfo')} className="mb-4">
        <Descriptions column={2}>
          <Descriptions.Item label={t('dspBill.fields.billId')}>{bill.id}</Descriptions.Item>
          <Descriptions.Item label={t('dspBill.fields.dspCustomer')}>{bill.clientName}</Descriptions.Item>
          <Descriptions.Item label={t('dspBill.fields.settlementClientId')}>{bill.clientId}</Descriptions.Item>
          <Descriptions.Item label={t('dspBill.fields.clientId')}>{bill.channelId}</Descriptions.Item>
          <Descriptions.Item label={t('dspBill.fields.billType')}>{renderBillTypeTag(bill.billType)}</Descriptions.Item>
          <Descriptions.Item label={t('dspBill.list.billStatus')}>
            {bill.isCanceled ? (
              <Tag color="red">{t('enum.billStatus.CANCELED')}</Tag>
            ) : (
              <Tag color={getBillStatusColor(bill.status)}>
                {bill.status ? t(`enum.billStatus.${bill.status}`, { defaultValue: bill.status }) : ''}
              </Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('dspBill.fields.billPeriod')}>
            {`${formatBillPeriodTime(bill.periodStart, bill.timeZone)} - ${formatBillPeriodTime(bill.periodEnd, bill.timeZone)}`}
          </Descriptions.Item>
          <Descriptions.Item label={t('dspBill.fields.timezone')}>
            {bill.timeZone || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('dspBill.fields.billAmount')}>
            <span className="text-lg font-bold text-red-600">{formatSettlementAmountFromMajor(bill.billAmount, bill.currency)}</span>
          </Descriptions.Item>
          <Descriptions.Item label={t('dspBill.fields.createdAtLocal')}>
            {bill.createdAt ? new Date(bill.createdAt).toLocaleString(i18n.language, { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('dspBill.fields.updatedAtLocal')}>
            {bill.updatedAt ? new Date(bill.updatedAt).toLocaleString(i18n.language, { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }) : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <DspBillPaymentInfoCard
        billId={billId!}
        billStatus={bill.status}
        paymentInfo={paymentInfoData?.paymentInfo}
        loading={paymentInfoLoading}
        canOperate={false}
        dspOwnerView
      />

      <Card title={t('dspBill.detail.feeSummary')} className="mb-4">
        <Table
          columns={feeSummaryColumns}
          dataSource={visibleFeeSummaries}
          rowKey="feeItemId"
          pagination={false}
          size="small"
        />
      </Card>

      <Card title="附件" className="mb-4">
        <BillAttachmentPanel billId={billId!} billStatus={bill.status} readOnly />
      </Card>

      <Card title={t('dspBill.detail.exportHistory')} className="mb-4">
        <div className="mb-4 flex justify-end">
          <Button icon={<ReloadOutlined />} onClick={handleRefreshExportHistory} loading={exportHistoryLoading}>
            {t('dspBill.exportHistory.refresh')}
          </Button>
        </div>
        <Table
          columns={exportHistoryColumns}
          dataSource={exportHistory}
          rowKey="taskId"
          loading={exportHistoryLoading}
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => t('table.pagination.totalItems', { total }),
            defaultPageSize: 10,
            pageSizeOptions: ['10', '20', '50']
          }}
          size="small"
          scroll={{ x: 1000 }}
        />
      </Card>

      <BillExportModal visible={exportModalVisible} bill={bill} onCancel={() => setExportModalVisible(false)} onExport={handleExport} />
      <BillExportProgressModal
        taskId={exportTaskId}
        visible={progressModalVisible}
        onClose={() => { setProgressModalVisible(false); setExportTaskId(null); }}
        onCompleted={handleExportCompleted}
      />
    </div>
  );
};

export default DeliveryDspBillDetailPage;
